// Service de synchronisation pour envoyer les données hors ligne au serveur
import { 
  getUnsyncedMembres, 
  getUnsyncedPaiements, 
  markMembreSynced, 
  markPaiementSynced,
  getPendingSyncCount
} from './offlineStorage';
import { createMembre, createPaiement } from './api';
import { notificationService } from '../utils/notifications';

let isSyncing = false;
let syncInterval = null;

// Vérifier si l'application est en ligne
export const isOnline = () => {
  return navigator.onLine;
};

// Écouter les changements de statut de connexion
export const setupConnectionListener = (onStatusChange) => {
  window.addEventListener('online', () => {
    console.log('🌐 Connexion rétablie - Démarrage de la synchronisation');
    onStatusChange(true);
    syncAll();
  });

  window.addEventListener('offline', () => {
    console.log('📴 Connexion perdue - Mode hors ligne activé');
    onStatusChange(false);
  });
};

// Synchroniser un membre
const syncMembre = async (membreData) => {
  try {
    console.log('🔄 Synchronisation du membre:', membreData.id);
    
    // Préparer les données pour l'API
    const formData = new FormData();
    
    // Ajouter tous les champs (sauf les fichiers et métadonnées)
    Object.keys(membreData).forEach(key => {
      if (key !== 'id' && 
          key !== 'timestamp' && 
          key !== 'synced' && 
          key !== 'offline' && 
          key !== 'serverId' &&
          key !== 'photo' && 
          key !== 'photo_signature') {
        if (membreData[key] !== null && membreData[key] !== undefined && membreData[key] !== '') {
          formData.append(key, membreData[key]);
        }
      }
    });

    // Gérer les fichiers (photo et signature)
    if (membreData.photo) {
      if (typeof membreData.photo === 'string' && membreData.photo.startsWith('blob:')) {
        // Si c'est une URL blob, convertir en File
        try {
          const response = await fetch(membreData.photo);
          const blob = await response.blob();
          const file = new File([blob], 'photo.jpg', { type: blob.type || 'image/jpeg' });
          formData.append('photo', file);
        } catch (error) {
          console.warn('Impossible de convertir la photo en fichier:', error);
        }
      } else if (membreData.photo instanceof File) {
        // Si c'est déjà un File, l'ajouter directement
        formData.append('photo', membreData.photo);
      }
    }

    if (membreData.photo_signature) {
      if (typeof membreData.photo_signature === 'string' && membreData.photo_signature.startsWith('blob:')) {
        try {
          const response = await fetch(membreData.photo_signature);
          const blob = await response.blob();
          const file = new File([blob], 'signature.jpg', { type: blob.type || 'image/jpeg' });
          formData.append('photo_signature', file);
        } catch (error) {
          console.warn('Impossible de convertir la signature en fichier:', error);
        }
      } else if (membreData.photo_signature instanceof File) {
        formData.append('photo_signature', membreData.photo_signature);
      }
    }

    // Envoyer au serveur
    const response = await createMembre(formData);
    
    if (response && response.data && response.data.membre) {
      // Marquer comme synchronisé
      await markMembreSynced(membreData.id, response.data.membre.id);
      console.log('✅ Membre synchronisé avec succès:', response.data.membre.id);
      return { success: true, membre: response.data.membre };
    }
    
    throw new Error('Réponse invalide du serveur');
  } catch (error) {
    console.error('❌ Erreur lors de la synchronisation du membre:', error);
    throw error;
  }
};

// Synchroniser un paiement
const syncPaiement = async (paiementData) => {
  try {
    console.log('🔄 Synchronisation du paiement:', paiementData.id);
    
    // Préparer les données pour l'API
    const formData = new FormData();
    
    Object.keys(paiementData).forEach(key => {
      if (key !== 'id' && key !== 'timestamp' && key !== 'synced' && key !== 'offline' && key !== 'serverId') {
        if (paiementData[key] !== null && paiementData[key] !== undefined && paiementData[key] !== '') {
          formData.append(key, paiementData[key]);
        }
      }
    });

    // Envoyer au serveur
    const response = await createPaiement(formData);
    
    if (response && response.data && response.data.paiement) {
      // Marquer comme synchronisé
      await markPaiementSynced(paiementData.id, response.data.paiement.id);
      console.log('✅ Paiement synchronisé avec succès:', response.data.paiement.id);
      return { success: true, paiement: response.data.paiement };
    }
    
    throw new Error('Réponse invalide du serveur');
  } catch (error) {
    console.error('❌ Erreur lors de la synchronisation du paiement:', error);
    throw error;
  }
};

// Synchroniser tous les membres en attente
export const syncAllMembres = async () => {
  if (!isOnline()) {
    console.log('📴 Hors ligne - Impossible de synchroniser');
    return { success: false, message: 'Hors ligne' };
  }

  try {
    const membres = await getUnsyncedMembres();
    console.log(`🔄 Synchronisation de ${membres.length} membre(s)...`);

    const results = [];
    for (const membre of membres) {
      try {
        const result = await syncMembre(membre);
        results.push({ success: true, membre: result.membre });
      } catch (error) {
        console.error(`❌ Erreur pour le membre ${membre.id}:`, error);
        results.push({ success: false, membre: membre, error: error.message });
      }
    }

    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;

    if (successCount > 0) {
      notificationService.success(
        'Synchronisation réussie', 
        `${successCount} membre(s) synchronisé(s) avec succès`
      );
    }

    if (failCount > 0) {
      notificationService.warning(
        'Synchronisation partielle', 
        `${failCount} membre(s) n'ont pas pu être synchronisé(s)`
      );
    }

    return { success: true, results, successCount, failCount };
  } catch (error) {
    console.error('❌ Erreur lors de la synchronisation des membres:', error);
    return { success: false, error: error.message };
  }
};

// Synchroniser tous les paiements en attente
export const syncAllPaiements = async () => {
  if (!isOnline()) {
    console.log('📴 Hors ligne - Impossible de synchroniser');
    return { success: false, message: 'Hors ligne' };
  }

  try {
    const paiements = await getUnsyncedPaiements();
    console.log(`🔄 Synchronisation de ${paiements.length} paiement(s)...`);

    const results = [];
    for (const paiement of paiements) {
      try {
        const result = await syncPaiement(paiement);
        results.push({ success: true, paiement: result.paiement });
      } catch (error) {
        console.error(`❌ Erreur pour le paiement ${paiement.id}:`, error);
        results.push({ success: false, paiement: paiement, error: error.message });
      }
    }

    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;

    if (successCount > 0) {
      notificationService.success(
        'Synchronisation réussie', 
        `${successCount} paiement(s) synchronisé(s) avec succès`
      );
    }

    if (failCount > 0) {
      notificationService.warning(
        'Synchronisation partielle', 
        `${failCount} paiement(s) n'ont pas pu être synchronisé(s)`
      );
    }

    return { success: true, results, successCount, failCount };
  } catch (error) {
    console.error('❌ Erreur lors de la synchronisation des paiements:', error);
    return { success: false, error: error.message };
  }
};

// Synchroniser toutes les données en attente
export const syncAll = async () => {
  if (isSyncing) {
    console.log('⏳ Synchronisation déjà en cours...');
    return;
  }

  if (!isOnline()) {
    console.log('📴 Hors ligne - Synchronisation reportée');
    return;
  }

  isSyncing = true;
  console.log('🚀 Démarrage de la synchronisation complète...');

  try {
    // Synchroniser d'abord les membres (car les paiements peuvent dépendre des membres)
    await syncAllMembres();
    
    // Puis synchroniser les paiements
    await syncAllPaiements();

    const pendingCount = await getPendingSyncCount();
    if (pendingCount === 0) {
      notificationService.success('Synchronisation terminée', 'Toutes les données ont été synchronisées');
    }

    console.log('✅ Synchronisation complète terminée');
  } catch (error) {
    console.error('❌ Erreur lors de la synchronisation complète:', error);
    notificationService.error('Erreur de synchronisation', 'Une erreur est survenue lors de la synchronisation');
  } finally {
    isSyncing = false;
  }
};

// Démarrer la synchronisation automatique périodique
export const startAutoSync = (interval = 30000) => { // 30 secondes par défaut
  if (syncInterval) {
    clearInterval(syncInterval);
  }

  syncInterval = setInterval(() => {
    if (isOnline() && !isSyncing) {
      syncAll();
    }
  }, interval);

  console.log('🔄 Synchronisation automatique démarrée');
};

// Arrêter la synchronisation automatique
export const stopAutoSync = () => {
  if (syncInterval) {
    clearInterval(syncInterval);
    syncInterval = null;
    console.log('⏹️ Synchronisation automatique arrêtée');
  }
};

// Synchronisation manuelle (appelée par l'utilisateur)
export const manualSync = async () => {
  if (!isOnline()) {
    notificationService.error('Hors ligne', 'Impossible de synchroniser sans connexion internet');
    return { success: false, message: 'Hors ligne' };
  }

  notificationService.info('Synchronisation', 'Synchronisation en cours...');
  return await syncAll();
};

